import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardHeader, CardContent } from '@/components/ui/card'

interface FormSkeletonProps {
  rows?: number
  columns?: number
  /** False on pages that draw their own title above the skeleton. */
  header?: boolean
  /** Adds a side card, for pages split into two equal columns (e.g. a usage panel). */
  aside?: boolean
}

function SkeletonCard({ rows, columns }: { rows: number; columns: number }) {
  return (
    <Card className="min-w-0 flex-1">
      <CardHeader>
        <Skeleton className="h-[1.2rem] w-40" />
      </CardHeader>
      <CardContent>
        <div className={columns === 3 ? 'field-grid field-grid--3' : 'field-grid'}>
          {Array.from({ length: rows * columns }).map((_, i) => (
            <div className="p-field" key={i}>
              <Skeleton className="mb-2 h-3 w-20" />
              <Skeleton className="h-[2.2rem] w-full rounded-md" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Stands in for an edit page while it loads. It sits in the same `.ad-page`
 * frame the loaded page uses, so nothing shifts sideways once the real form
 * arrives, and it fakes the page title as well as the card.
 */
export function FormSkeleton({
  rows = 3,
  columns = 2,
  header = true,
  aside = false,
}: FormSkeletonProps) {
  return (
    <div className="ad-page">
      {header && (
        <div className="ad-page__header">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-[22px] w-64" />
            <Skeleton className="h-3 w-40" />
          </div>
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      )}
      {aside ? (
        <div className="ad-edit-columns">
          <SkeletonCard rows={rows} columns={columns} />
          <SkeletonCard rows={rows} columns={1} />
        </div>
      ) : (
        <SkeletonCard rows={rows} columns={columns} />
      )}
    </div>
  )
}
